import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

type ModelState = {
  id: string
  kind: string
  status: 'pending' | 'checking' | 'downloading' | 'ready' | 'error'
  detail: string
  percent: number | null
  error: string | null
}

type Status = { ready: boolean; models: ModelState[]; program?: string }

const ProgramName = createContext('Assistant')

/** The name from the identity map. The gate loads it with the model status. */
export function useProgramName(): string {
  return useContext(ProgramName)
}

const POLL_MS = 1000

/** Blocks the console until the backend reports every required model is installed. */
export default function ModelGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status | null>(null)
  const [unreachable, setUnreachable] = useState(false)

  useEffect(() => {
    let stop = false
    let timer = 0
    const poll = async () => {
      try {
        const res = await fetch('/api/models/status')
        if (!res.ok) throw new Error(String(res.status))
        const next = (await res.json()) as Status
        if (stop) return
        setStatus(next)
        setUnreachable(false)
        if (next.ready) return
      } catch {
        if (stop) return
        setUnreachable(true)
      }
      timer = window.setTimeout(poll, POLL_MS)
    }
    void poll()
    return () => {
      stop = true
      window.clearTimeout(timer)
    }
  }, [])

  const gate = status?.ready ? (
    children
  ) : (
    <div className="model-gate" data-testid="model-gate" role="status" aria-live="polite">
      <div className="model-gate-card">
        <h2>Preparing {status?.program || 'the assistant'}</h2>
        <p>
          {unreachable
            ? 'Waiting for the backend on port 8801…'
            : 'Downloading the models the assistant needs. Nothing starts until they are all installed.'}
        </p>
        <ul>
          {(status?.models ?? []).map((m) => (
            <li key={m.id} className={`model-row ${m.status}`}>
              <div className="model-row-head">
                <span className="model-row-kind">{m.kind}</span>
                <span className="model-row-state">{m.status}</span>
              </div>
              <code>{m.id}</code>
              <div className={`model-bar${m.percent === null && m.status === 'downloading' ? ' indeterminate' : ''}`}>
                <span style={{ width: `${m.status === 'ready' ? 100 : (m.percent ?? 0)}%` }} />
              </div>
              <small>
                {m.status === 'error' ? m.detail : m.detail}
                {m.percent !== null && m.status === 'downloading' ? ` · ${m.percent}%` : ''}
              </small>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
  return <ProgramName.Provider value={status?.program || 'Assistant'}>{gate}</ProgramName.Provider>
}
